/* eslint-disable no-unused-vars */

import { getValue } from 'firebase/remote-config';
import { getRemoteConfigClient } from '../apis/firebase';

/**
 * Reads one Remote Config value, degrading to an empty value object when
 * Firebase is not configured (tests, a missing API key) so every getter below
 * falls back to its own default instead of throwing.
 *
 * @param {string} key
 * @returns {{ asString: () => string, asNumber: () => number, asBoolean: () => boolean }}
 */
const readValue = (key) => {
  const client = getRemoteConfigClient();
  if (!client) return { asString: () => '', asNumber: () => 0, asBoolean: () => false };
  return getValue(client, key);
};

/**
 * Keys for Firebase Remote Config keys.
 * Can be used when calling {@link remoteConfig}'s `getValue()`
 *
 * @enum {string}
 */
const RemoteConfigKeys = {
  ExmapleKey: 'EXAMPLE_KEY',
  CredentialV2Admin: 'CREDENTIAL_V2_ADMIN',
  OnsiteInterviewThresholdInMinutes: 'ONSITE_INTERVIEW_THRESHOLD_MINUTES',
  ShowClockInOutW2States: 'SHOW_CLOCK_IN_OUT_W2_STATES',
  DsoNameConsents: 'DSO_NAME_CONSENTS',
  DeleteReviewThreshold: 'DELETE_REVIEW_THRESHOLD',
  CSPhoneNumber: 'CS_PHONE_NUMBER',
};

/**
 * @typedef {Object} CredentialingV2Config
 * @property {string} minAppVersion - Minimum client app version required (semver, e.g. "3.78.0").
 * @property {string[]} states - Allowed US state codes (e.g. ["CA", "NY"]). Empty array means all states allowed.
 */

/**
 * Utility object for accessing Firebase Remote Config values.
 * Provides type-safe methods to retrieve various configuration parameters.
 */
export const RemoteConfigUtils = {
  getExample: () => readValue(RemoteConfigKeys.ExmapleKey).asNumber(),

  /**
   * Reads the `CREDENTIAL_V2_ADMIN` remote config as a JSON object.
   * Shape: `{ minAppVersion: string, states: string[] }`.
   * Returns safe defaults if the value is missing or malformed.
   * @returns {CredentialingV2Config}
   */
  getCredentialingV2Config: () => {
    const raw = readValue(RemoteConfigKeys.CredentialV2Admin).asString();
    try {
      const parsed = raw ? JSON.parse(raw) : {};
      return {
        minAppVersion: typeof parsed.minAppVersion === 'string' ? parsed.minAppVersion : '',
        states: Array.isArray(parsed.states) ? parsed.states : [],
      };
    } catch {
      return { minAppVersion: '', states: [] };
    }
  },

  getOnsiteInterviewThresholdInMinutes: () =>
    readValue(RemoteConfigKeys.OnsiteInterviewThresholdInMinutes).asNumber(),

  /**
   * How long after a shift ends an admin may still delete its review.
   *
   * The same `DELETE_REVIEW_THRESHOLD` key the monolith reads to compute
   * `isRemovable` server-side (`tempmee_server/src/services/user/users-review.types.ts:80-92`).
   * The office Reviews table now reads reviews from organization-service, whose
   * payload carries no removability flag, so the portal evaluates the same gate
   * against the same config value — see `isReviewRemovable`.
   *
   * The key name carries no unit, unlike `ONSITE_INTERVIEW_THRESHOLD_MINUTES`
   * above. Hours, confirmed against the monolith's own comparison:
   * `tempmee_server/src/services/user/users-review.types.ts:88`.
   *
   * Returns 0 when the key is unset or Firebase is unconfigured, which
   * `isReviewRemovable` treats as "nothing is removable" — the fail-closed
   * direction. That makes an unpublished key visible in QA (the Remove icon
   * disappears) rather than silently widening who can delete what.
   *
   * @returns {number}
   */
  getDeleteReviewThresholdInHours: () =>
    readValue(RemoteConfigKeys.DeleteReviewThreshold).asNumber(),

  /**
   * Reads `CS_PHONE_NUMBER` (shared with mobile) as stored, e.g. "(855) 879-4688".
   * `getCSPhone` in NumberUtils normalises it and owns the fallback.
   * @returns {string}
   */
  getCSPhoneNumber: () => readValue(RemoteConfigKeys.CSPhoneNumber).asString(),

  getShowClockInOutW2States: () =>
    readValue(RemoteConfigKeys.ShowClockInOutW2States).asString()?.split(','),

  /**
   * Reads the `DSO_NAME_CONSENTS` remote config as a JSON object.
   * Shape: `{ [dsoCode: string]: string[] }` where keys are DSO codes and values are state arrays.
   * Returns an empty object if the value is missing or malformed.
   * @returns {Record<string, string[]>}
   */
  getDsoNameConsents: () => {
    const raw = readValue(RemoteConfigKeys.DsoNameConsents).asString();
    try {
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  },
};

/**
 * Evaluates whether credentialing v2 is enabled for a user given the remote config.
 * Requires the client version to meet `minAppVersion` and the user's state to be in
 * the `states` list. An empty `states` list is treated as "all states allowed".
 *
 * @param {Object} params
 * @param {string | undefined} params.clientVersion
 * @param {string | undefined} params.userState - US state code, e.g. "CA"
 * @param {CredentialingV2Config} params.config
 * @returns {boolean}
 */
export const isCredentialingV2AllowedByConfig = ({ clientVersion, userState, config }) => {
  if (!isVersionAtLeast(clientVersion, config?.minAppVersion)) return false;
  const states = config?.states ?? [];
  if (states.length === 0) return true;
  return Boolean(userState) && states.includes(userState);
};

/**
 * Compares two semver version strings (e.g. "1.2.3").
 * Returns true if `version` is greater than or equal to `minVersion`.
 */
export const isVersionAtLeast = (version, minVersion) => {
  if (!version || !minVersion) return false;

  const vParts = version.split('.').map(Number);
  const mParts = minVersion.split('.').map(Number);
  const length = Math.max(vParts.length, mParts.length);

  for (let i = 0; i < length; i++) {
    const v = vParts[i] || 0;
    const m = mParts[i] || 0;
    if (v > m) return true;
    if (v < m) return false;
  }

  return true;
};
